import $ from 'jquery';
import 'bootstrap-table/dist/bootstrap-table.css';
import 'bootstrap-table/dist/bootstrap-table';
import 'bootstrap-table/dist/extensions/filter-control/bootstrap-table-filter-control.css';
import 'bootstrap-table/dist/extensions/filter-control/bootstrap-table-filter-control';
import 'bootstrap-table/dist/extensions/sticky-header/bootstrap-table-sticky-header.css';
import 'bootstrap-table/dist/extensions/sticky-header/bootstrap-table-sticky-header';

// bootstrap-table@1.27.1 no longer ships the legacy natural-sorting dist extension,
// so formatted numeric columns use the shared numeric sorter below.

const ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT = false;
const ENABLE_STICKY_HEADERS = true;
const MIN_TABLE_HEIGHT = 260;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 100;
const VIEWPORT_MARGIN = 24;
const RESIZE_DEBOUNCE_MS = 120;
const BASE_PAGE_LIST = [10, 25, 50, 100, 'All'];

const TABLE_STATES = new WeakMap();

const DETAIL_ICON = [
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-list-check" viewBox="0 0 16 16">',
  '<path fill-rule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5M3.854 2.146a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 1 1 .708-.708L2 3.293l1.146-1.147a.5.5 0 0 1 .708 0m0 4a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 1 1 .708-.708L2 7.293l1.146-1.147a.5.5 0 0 1 .708 0m0 4a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0"/>',
  '</svg>',
].join('');

const CHECK_ICON = [
  '<span class="text-info">',
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">',
  '<path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>',
  '</svg>',
  '</span>',
].join('');

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value) {
  return escapeHtml(value);
}

function stripHtml(value) {
  return $('<div>').html(String(value ?? '')).text().trim();
}

function splitPipeValues(value) {
  return String(value ?? '')
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseNumber(value) {
  const normalized = stripHtml(value)
    .replace(/\$/g, '')
    .replace(/,/g, '')
    .replace(/[^0-9.\-]/g, '')
    .trim();

  if (!normalized) {
    return null;
  }

  const number = Number.parseFloat(normalized);
  return Number.isFinite(number) ? number : null;
}

function getCellData(row, field) {
  return row[`_${field}_data`] || {};
}

function formatBadge(value, className) {
  return `<span class="badge ${className}">${escapeHtml(value)}</span>`;
}

function renderBadgeList(values, className) {
  return values.map((item) => formatBadge(item, className)).join('<br>');
}

function buildDetailsButton(url) {
  return [
    `<a class="btn btn-outline-primary" role="button" href="${escapeAttr(url)}">`,
    DETAIL_ICON,
    '</a>',
  ].join('');
}

function linkFormatter(value, row, index, field) {
  const cellData = getCellData(row, field);
  const label = escapeHtml(value);

  if (!cellData.url) {
    return label;
  }

  return `<a href="${escapeAttr(cellData.url)}">${label}</a>`;
}

function muniSystemFormatter(value, row, index, field) {
  const values = splitPipeValues(getCellData(row, field).values || value);
  return values
    .map((item) => {
      if (item === 'Combined') {
        return '<span class="badge" style="color:slategray;background-color:#ead7eb;">Combined</span>';
      }
      if (item === 'Separate') {
        return '<span class="badge" style="color:slategray;background-color:#bcdce7;">Separate</span>';
      }
      if (item === 'Hybrid') {
        return '<span class="badge text-bg-secondary">Hybrid</span>';
      }
      return formatBadge(item, 'text-bg-secondary');
    })
    .join(' ');
}

function pocSystemFormatter(value, row, index, field) {
  return splitPipeValues(getCellData(row, field).values || value)
    .map((item) => {
      if (item === 'CS') {
        return '<span class="badge" style="color:slategray;background-color:#ead7eb;">CS</span>';
      }
      if (item === 'SS') {
        return '<span class="badge" style="color:slategray;background-color:#bcdce7;">SS</span>';
      }
      return formatBadge(item, 'text-bg-secondary');
    })
    .join(' ');
}

function statusFormatter(value, row, index, field) {
  return splitPipeValues(getCellData(row, field).values || value)
    .map((item) => {
      const className = item.includes('Projects Submitted') ? 'text-bg-primary' : 'text-bg-success';
      return formatBadge(item, className);
    })
    .join(' ');
}

function badgeListFormatter(value) {
  const values = splitPipeValues(value);
  return values.length ? renderBadgeList(values, 'text-bg-secondary') : '';
}

function growFormatter(value, row, index, field) {
  const labels = splitPipeValues(getCellData(row, field).labels);

  if (!labels.length) {
    return String(value ?? '').toLowerCase() === 'yes' ? formatBadge('Yes', 'text-bg-success') : '';
  }

  return renderBadgeList(labels, 'text-bg-success');
}

function booleanIconFormatter(value) {
  return String(value ?? '').toLowerCase() === 'yes' ? CHECK_ICON : '';
}

function integerFormatter(value) {
  const number = parseNumber(value);
  return number === null ? '' : Math.round(number).toLocaleString();
}

function currencyFormatter(value) {
  const number = parseNumber(value);
  return number === null ? '-' : `$${Math.round(number).toLocaleString()}`;
}

function reductionFormatter(value, row, index, field) {
  const number = parseNumber(value);
  if (number === null) {
    return '';
  }

  const unit = getCellData(row, field).unit || '';
  const renderedNumber = number.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const renderedUnit = unit ? ` <span class="small text-muted">${escapeHtml(unit)}</span>` : '';

  return `${renderedNumber}${renderedUnit}`;
}

function coloredIntegerFormatter(value, row, index, field) {
  const rendered = integerFormatter(value);
  if (!rendered) {
    return '';
  }

  const cellData = getCellData(row, field);
  const style = [
    cellData.bgColor ? `background-color:${cellData.bgColor}` : '',
    cellData.textColor ? `color:${cellData.textColor}` : '',
  ]
    .filter(Boolean)
    .join(';');

  if (!style) {
    return rendered;
  }

  return `<span style="${escapeAttr(style)}">${rendered}</span>`;
}

function detailsFormatter(value, row, index, field) {
  const cellData = getCellData(row, field);
  return cellData.url ? buildDetailsButton(cellData.url) : '';
}

function pipeValuesCollector(fieldValue) {
  return splitPipeValues(fieldValue);
}

function tributaryValuesCollector(fieldValue, row) {
  const values = getCellData(row, 'tributaries').values || fieldValue;
  return splitPipeValues(values);
}

function pipeContains(searchValue, value) {
  if (!searchValue) {
    return true;
  }

  const target = String(searchValue).toLowerCase();
  return splitPipeValues(value).some((item) => item.toLowerCase() === target);
}

function tributaryContains(searchValue, value, row) {
  if (!searchValue) {
    return true;
  }

  const target = String(searchValue).toLowerCase();
  const values = getCellData(row, 'tributaries').values || value;

  return splitPipeValues(values).some((item) => item.toLowerCase() === target);
}

function glsLabelCollector(fieldValue, row) {
  return [row.gls_label || 'no data'];
}

function glsLabelContains(searchValue, value, fieldName, allData) {
  if (!searchValue) {
    return true;
  }

  // filterCustomSearch receives (searchValue, cellValue, fieldName, allData) —
  // the 3rd arg is the field name string, not the row. Find the row via actual_ii.
  const row = (allData || []).find((r) => r.actual_ii === value);
  return (row?.gls_label || 'no data') === searchValue;
}

function numericSorter(a, b) {
  const left = parseNumber(a);
  const right = parseNumber(b);

  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return -1;
  }
  if (right === null) {
    return 1;
  }

  return left - right;
}

function textSorter(a, b) {
  return stripHtml(a).localeCompare(stripHtml(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function buildPageList(pageSize) {
  const numericValues = BASE_PAGE_LIST.filter((value) => typeof value === 'number');

  if (typeof pageSize === 'number' && Number.isFinite(pageSize)) {
    numericValues.push(pageSize);
  }

  const uniqueValues = [...new Set(numericValues)].sort((left, right) => left - right);
  return [...uniqueValues, 'All'];
}

function samePageList(left, right) {
  return JSON.stringify(left || []) === JSON.stringify(right || []);
}

function processHtmxTableContent(table) {
  if (typeof window.htmx?.process !== 'function') {
    return;
  }

  const root = table.closest('.bootstrap-table') || table;
  window.htmx.process(root);
}

function whenBootstrapTableReady(table, callback, attemptsRemaining = 20) {
  const $table = $(table);

  if ($table.data('bootstrap.table')) {
    callback($table);
    return;
  }

  if (attemptsRemaining <= 0) {
    return;
  }

  window.requestAnimationFrame(() => {
    whenBootstrapTableReady(table, callback, attemptsRemaining - 1);
  });
}

function getStickyHeaderOptions() {
  return {
    stickyHeader: ENABLE_STICKY_HEADERS,
    stickyHeaderOffsetLeft: 0,
    stickyHeaderOffsetRight: 0,
    stickyHeaderOffsetY: 0,
  };
}

function measureRowHeight($root) {
  const $row = $root.find('.fixed-table-body tbody tr:visible').not('.no-records-found').first();
  const height = Math.round($row.outerHeight() || 0);
  return height > 0 ? height : 40;
}

function measureHeaderHeight($root) {
  return Math.round(
    $root.find('.fixed-table-header thead').first().outerHeight() ||
      $root.find('.fixed-table-body thead').first().outerHeight() ||
      44
  );
}

function computeResponsiveHeight(table, $root) {
  const shell = table.closest('[data-coa-summary-shell]') || $root.get(0);
  const shellRect = shell.getBoundingClientRect();
  const toolbarHeight = Math.round($root.find('.fixed-table-toolbar').first().outerHeight(true) || 0);
  const paginationHeight = Math.round($root.find('.fixed-table-pagination').first().outerHeight(true) || 0);
  const available = window.innerHeight - shellRect.top - toolbarHeight - paginationHeight - VIEWPORT_MARGIN;

  return Math.max(MIN_TABLE_HEIGHT, Math.floor(available));
}

function computeAutoPageSize($table, $root, height) {
  const totalRows = $table.bootstrapTable('getData', { unfiltered: true }).length;
  const rowHeight = measureRowHeight($root);
  const headerHeight = measureHeaderHeight($root);
  const bodyHeight = Math.max(rowHeight, height - headerHeight - 8);
  const computed = Math.max(MIN_PAGE_SIZE, Math.floor(bodyHeight / rowHeight));
  const bounded = Math.min(MAX_PAGE_SIZE, computed);

  if (!totalRows) {
    return bounded;
  }

  return Math.max(MIN_PAGE_SIZE, Math.min(totalRows, bounded));
}

function applyResponsiveLayout(table) {
  const $table = $(table);
  const state = TABLE_STATES.get(table);
  const instance = $table.data('bootstrap.table');

  if (!state || !instance || state.isApplyingLayout) {
    return;
  }

  const $root = $table.closest('.bootstrap-table');
  if (!$root.length) {
    return;
  }

  const height = computeResponsiveHeight(table, $root);
  const options = $table.bootstrapTable('getOptions');
  const pageSize = state.manualPageSize || computeAutoPageSize($table, $root, height);
  const pageList = buildPageList(pageSize);
  const shouldRefresh = options.pageSize !== pageSize || !samePageList(options.pageList, pageList);

  state.isApplyingLayout = true;
  state.autoPageSize = pageSize;

  try {
    if (shouldRefresh) {
      state.isAutoUpdatingPageSize = true;
      $table.bootstrapTable('refreshOptions', {
        pageList,
        pageSize,
      });
      state.isAutoUpdatingPageSize = false;
    }

    $table.bootstrapTable('resetView', { height });
  } finally {
    state.isApplyingLayout = false;
  }
}

function syncStickyHeader(table) {
  if (!ENABLE_STICKY_HEADERS) {
    return;
  }

  whenBootstrapTableReady(table, ($table) => {
    const instance = $table.data('bootstrap.table');
    if (!instance || !instance.options.stickyHeader) {
      return;
    }

    const stickyOptions = getStickyHeaderOptions();

    instance.options.stickyHeaderOffsetLeft = stickyOptions.stickyHeaderOffsetLeft;
    instance.options.stickyHeaderOffsetRight = stickyOptions.stickyHeaderOffsetRight;
    instance.options.stickyHeaderOffsetY = stickyOptions.stickyHeaderOffsetY;

    if (typeof instance.renderStickyHeader === 'function') {
      instance.renderStickyHeader();
    }
  });
}

function applyInitialTableOptions(table) {
  whenBootstrapTableReady(table, ($table) => {
    const options = $table.bootstrapTable('getOptions');
    const nextOptions = {};

    if (!ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT && (options.pagination || options.height)) {
      nextOptions.height = null;
      nextOptions.pagination = false;
    }

    if (ENABLE_STICKY_HEADERS && !options.stickyHeader) {
      Object.assign(nextOptions, getStickyHeaderOptions());
    }

    if (Object.keys(nextOptions).length > 0) {
      $table.bootstrapTable('refreshOptions', nextOptions);
    }

    if (!ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT) {
      $table.bootstrapTable('resetView');
    }

    syncStickyHeader(table);
  });
}

function normalizeTributaryFilterOptions(table) {
  const $table = $(table);
  const selects = $table
    .closest('.bootstrap-table')
    .find('select.bootstrap-table-filter-control-tributaries')
    .get();

  selects.forEach((select) => {
    const $select = $(select);
    const currentValue = String($select.val() ?? '').trim();
    const uniqueValues = new Set();

    Array.from(select.options).forEach((option) => {
      const rawValue = String(option.value || option.text || '').trim();
      if (!rawValue) {
        return;
      }

      splitPipeValues(rawValue).forEach((item) => {
        uniqueValues.add(item);
      });
    });

    const sortedValues = Array.from(uniqueValues).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    );

    select.options.length = 0;
    select.add(new Option(' ', ''));
    sortedValues.forEach((value) => {
      select.add(new Option(value, value));
    });

    if (currentValue && uniqueValues.has(currentValue)) {
      $select.val(currentValue);
    } else {
      $select.val('');
    }
  });
}

function refreshSummaryTable(table) {
  if (ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT) {
    applyResponsiveLayout(table);
  }

  if (ENABLE_STICKY_HEADERS) {
    syncStickyHeader(table);
  }
}

function bindExportButtons(root = document) {
  root.querySelectorAll('[data-coa-export-table]').forEach((button) => {
    if (button.dataset.coaExportBound === 'true') {
      return;
    }

    button.dataset.coaExportBound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const tableId = button.dataset.coaExportTable;
      const filename = button.dataset.coaExportFilename || `${tableId}.csv`;
      downloadTableCsv(tableId, filename);
    });
  });
}

function csvCell(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function formatExportValue(row, field) {
  const cellData = getCellData(row, field);
  const rawValue = row[field];

  if (field === 'details') {
    return cellData.url || '';
  }

  if (field === 'grow') {
    const labels = splitPipeValues(cellData.labels);
    return labels.length ? labels.join('; ') : rawValue;
  }

  if (field === 'reduction') {
    const unit = cellData.unit || '';
    return unit && rawValue ? `${rawValue} ${unit}` : rawValue;
  }

  if (typeof rawValue === 'string' && rawValue.includes('|')) {
    return splitPipeValues(rawValue).join('; ');
  }

  return stripHtml(rawValue);
}

function downloadTableCsv(tableId, filename) {
  const $table = $(`#${tableId}`);
  if (!$table.length || !$table.data('bootstrap.table')) {
    return;
  }

  const columns = $table
    .bootstrapTable('getVisibleColumns')
    .filter((column) => column.field);
  const rows = $table.bootstrapTable('getData');
  const lines = [];

  lines.push(columns.map((column) => csvCell(stripHtml(column.title))).join(','));

  rows.forEach((row) => {
    lines.push(
      columns
        .map((column) => csvCell(formatExportValue(row, column.field)))
        .join(',')
    );
  });

  const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function initSummaryTable(table) {
  if (table.dataset.coaSummaryBound === 'true') {
    return;
  }

  table.dataset.coaSummaryBound = 'true';

  const $table = $(table);
  $table.on('post-body.bs.table', () => {
    window.requestAnimationFrame(() => {
      refreshSummaryTable(table);
      processHtmxTableContent(table);
    });
  });
  $table.on('created-controls.bs.table', () => {
    normalizeTributaryFilterOptions(table);
  });

  if (ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT) {
    const state = {
      autoPageSize: null,
      isApplyingLayout: false,
      isAutoUpdatingPageSize: false,
      manualPageSize: null,
    };

    TABLE_STATES.set(table, state);

    $table.on('page-change.bs.table', (event, pageNumber, pageSize) => {
      if (state.isApplyingLayout || state.isAutoUpdatingPageSize) {
        return;
      }

      if (pageSize !== state.autoPageSize) {
        state.manualPageSize = pageSize;
      }
    });
  }

  if (!ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT) {
    table.dataset.pagination = 'false';
    table.removeAttribute('data-pagination-v-align');
  }

  applyInitialTableOptions(table);

  if ($table.data('bootstrap.table')) {
    window.requestAnimationFrame(() => {
      normalizeTributaryFilterOptions(table);
      refreshSummaryTable(table);
    });
  }
}

function bindStickyHeaderObservers(root = document) {
  if (!ENABLE_STICKY_HEADERS) {
    return;
  }

  root.querySelectorAll('.navbar-collapse').forEach((element) => {
    if (element.dataset.coaStickyObserverBound === 'true') {
      return;
    }

    element.dataset.coaStickyObserverBound = 'true';

    ['shown.bs.collapse', 'hidden.bs.collapse'].forEach((eventName) => {
      element.addEventListener(eventName, debouncedSummaryTableRefresh);
    });
  });
}

function renderCombinedModalTemplate(modal, templateId) {
  const content = modal.querySelector('#coaCombinedRecordModalContent');
  const template = document.getElementById(templateId);

  if (!content || !template) {
    return;
  }

  content.innerHTML = template.innerHTML;
}

function isCombinedModalTarget(event) {
  return event.detail.target?.id === 'coaCombinedRecordModalContent';
}

function resetCombinedSummaryModal(modal, templateId = 'coaCombinedRecordModalLoadingTemplate') {
  renderCombinedModalTemplate(modal, templateId);
}

function initCombinedSummaryModal(root = document) {
  const modal = root.querySelector('#coaCombinedRecordModal');
  if (!modal || modal.dataset.coaModalBound === 'true') {
    return;
  }

  modal.dataset.coaModalBound = 'true';

  modal.addEventListener('show.bs.modal', () => {
    resetCombinedSummaryModal(modal);
  });

  modal.addEventListener('hidden.bs.modal', () => {
    resetCombinedSummaryModal(modal);
  });

  document.body.addEventListener('htmx:beforeRequest', (event) => {
    if (!isCombinedModalTarget(event)) {
      return;
    }

    resetCombinedSummaryModal(modal);
  });

  document.body.addEventListener('htmx:responseError', (event) => {
    if (!isCombinedModalTarget(event)) {
      return;
    }

    resetCombinedSummaryModal(modal, 'coaCombinedRecordModalErrorTemplate');
  });
}

function initCoaSummaryTables(root = document) {
  root.querySelectorAll('table[data-coa-summary-table]').forEach((table) => {
    initSummaryTable(table);
  });

  bindStickyHeaderObservers(root);
  bindExportButtons(root);
  initCombinedSummaryModal(root);
}

const debouncedSummaryTableRefresh = (() => {
  let timeoutId = null;

  return () => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      document.querySelectorAll('table[data-coa-summary-table]').forEach((table) => {
        refreshSummaryTable(table);
      });
    }, RESIZE_DEBOUNCE_MS);
  };
})();

window.coaSummaryTables = {
  collectors: {
    glsLabel: glsLabelCollector,
    pipeValues: pipeValuesCollector,
    tributaryValues: tributaryValuesCollector,
  },
  config: {
    enablePaginationAndDynamicHeight: ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT,
    enableStickyHeaders: ENABLE_STICKY_HEADERS,
  },
  downloadCsv: downloadTableCsv,
  filters: {
    glsLabelContains,
    pipeContains,
    tributaryContains,
  },
  formatters: {
    badgeList: badgeListFormatter,
    booleanIcon: booleanIconFormatter,
    coloredInteger: coloredIntegerFormatter,
    currency: currencyFormatter,
    details: detailsFormatter,
    grow: growFormatter,
    integer: integerFormatter,
    link: linkFormatter,
    muniSystem: muniSystemFormatter,
    pocSystem: pocSystemFormatter,
    reduction: reductionFormatter,
    status: statusFormatter,
  },
  init: initCoaSummaryTables,
  sorters: {
    numeric: numericSorter,
    text: textSorter,
  },
};

document.addEventListener('DOMContentLoaded', () => {
  initCoaSummaryTables();
});

if (ENABLE_PAGINATION_AND_DYNAMIC_HEIGHT) {
  window.addEventListener('resize', debouncedSummaryTableRefresh);
} else if (ENABLE_STICKY_HEADERS) {
  window.addEventListener('resize', debouncedSummaryTableRefresh);
}
